import * as XLSX from 'xlsx';
import { getCurrentDateForFilename } from './export-utils';

export interface StockTakeExportData {
  setupData: {
    countDate: string;
    warehouse: string;
    assignedTo: string;
  };
  countedItems: Array<{
    id: string;
    itemCode: string;
    description: string;
    category: string;
    systemQty: number;
    countedQty: number;
    variance: number;
    countedBy: string;
    warehouse: string;
    unit: string;
  }>;
  statistics: {
    totalItems: number;
    itemsMatching: number;
    totalShortage: number;
    totalOverage: number;
    shortageItems: number;
    overageItems: number;
  };
  notes?: string;
}

// Export stock take to PDF (placeholder - would need a PDF library)
export const exportStockTakeToPDF = async (
  data: StockTakeExportData,
  reference: string
): Promise<void> => {
  // This is a placeholder. In a real implementation, you would use a library like:
  // - jsPDF
  // - pdfmake
  // - react-pdf
  
  console.log('PDF export would be implemented here with data:', data);
  
  // For now, we'll create a simple HTML page and trigger print
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    throw new Error('Could not open print window');
  }
  
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Stock Take Report - ${reference}</title>
      <style>
        body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; }
        h1 { color: #333; }
        .header { margin-bottom: 30px; }
        .info-row { margin: 10px 0; }
        .label { font-weight: bold; display: inline-block; width: 150px; }
        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f2f2f2; }
        .variance-positive { color: #2563eb; }
        .variance-negative { color: #dc2626; }
        .variance-zero { color: #16a34a; }
        .statistics { margin: 20px 0; padding: 15px; background: #f9f9f9; border-radius: 5px; }
        .stat-row { margin: 8px 0; }
        @media print {
          button { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>Stock Take Report</h1>
        <div class="info-row"><span class="label">Reference:</span> ${reference}</div>
        <div class="info-row"><span class="label">Count Date:</span> ${new Date(data.setupData.countDate).toLocaleDateString()}</div>
        <div class="info-row"><span class="label">Warehouse:</span> ${data.setupData.warehouse || 'All Warehouses'}</div>
        <div class="info-row"><span class="label">Assigned To:</span> ${data.setupData.assignedTo || 'N/A'}</div>
        <div class="info-row"><span class="label">Generated:</span> ${new Date().toLocaleString()}</div>
      </div>
      
      <div class="statistics">
        <h2>Summary Statistics</h2>
        <div class="stat-row"><span class="label">Total Items:</span> ${data.statistics.totalItems}</div>
        <div class="stat-row"><span class="label">Items Matching:</span> ${data.statistics.itemsMatching} (${((data.statistics.itemsMatching / data.statistics.totalItems) * 100).toFixed(1)}%)</div>
        <div class="stat-row"><span class="label">Total Shortage:</span> ${data.statistics.totalShortage} units (${data.statistics.shortageItems} items)</div>
        <div class="stat-row"><span class="label">Total Overage:</span> ${data.statistics.totalOverage} units (${data.statistics.overageItems} items)</div>
      </div>
      
      <table>
        <thead>
          <tr>
            <th>Item Code</th>
            <th>Description</th>
            <th>Category</th>
            <th>System Qty</th>
            <th>Counted Qty</th>
            <th>Variance</th>
            <th>Counted By</th>
          </tr>
        </thead>
        <tbody>
          ${data.countedItems.map(item => `
            <tr>
              <td>${item.itemCode}</td>
              <td>${item.description}</td>
              <td>${item.category}</td>
              <td>${item.systemQty}</td>
              <td>${item.countedQty}</td>
              <td class="${item.variance === 0 ? 'variance-zero' : item.variance > 0 ? 'variance-positive' : 'variance-negative'}">
                ${item.variance === 0 ? 'Match' : (item.variance > 0 ? '+' : '') + item.variance}
              </td>
              <td>${item.countedBy}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      
      ${data.notes ? `
        <div style="margin-top: 30px;">
          <h2>Notes</h2>
          <p>${data.notes}</p>
        </div>
      ` : ''}
      
      <button onclick="window.print()" style="margin-top: 20px; padding: 10px 20px; font-size: 16px; cursor: pointer;">
        Print / Save as PDF
      </button>
    </body>
    </html>
  `;
  
  printWindow.document.write(html);
  printWindow.document.close();
};

// Export stock take to Excel
export const exportStockTakeToExcel = (
  data: StockTakeExportData,
  reference: string
): void => {
  const workbook = XLSX.utils.book_new();
  
  // Sheet 1: Summary
  const summaryData = [
    ['Stock Take Report'],
    [''],
    ['Reference', reference],
    ['Count Date', new Date(data.setupData.countDate).toLocaleDateString()],
    ['Warehouse', data.setupData.warehouse || 'All Warehouses'],
    ['Assigned To', data.setupData.assignedTo || 'N/A'],
    ['Generated', new Date().toLocaleString()],
    [''],
    ['Summary Statistics'],
    ['Total Items', data.statistics.totalItems],
    ['Items Matching', `${data.statistics.itemsMatching} (${((data.statistics.itemsMatching / data.statistics.totalItems) * 100).toFixed(1)}%)`],
    ['Total Shortage', `${data.statistics.totalShortage} units (${data.statistics.shortageItems} items)`],
    ['Total Overage', `${data.statistics.totalOverage} units (${data.statistics.overageItems} items)`],
  ];
  
  if (data.notes) {
    summaryData.push([''], ['Notes'], [data.notes]);
  }
  
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  
  // Set column widths
  summarySheet['!cols'] = [
    { wch: 20 },
    { wch: 50 },
  ];
  
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
  
  // Sheet 2: All Items
  const allItemsData = data.countedItems.map(item => ({
    'Item Code': item.itemCode,
    'Description': item.description,
    'Category': item.category,
    'Warehouse': item.warehouse,
    'Unit': item.unit,
    'System Qty': item.systemQty,
    'Counted Qty': item.countedQty,
    'Variance': item.variance,
    'Variance Status': item.variance === 0 ? 'Match' : item.variance > 0 ? 'Overage' : 'Shortage',
    'Counted By': item.countedBy,
  }));
  
  const allItemsSheet = XLSX.utils.json_to_sheet(allItemsData);
  allItemsSheet['!cols'] = [
    { wch: 15 },
    { wch: 30 },
    { wch: 15 },
    { wch: 20 },
    { wch: 10 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 15 },
    { wch: 20 },
  ];
  
  XLSX.utils.book_append_sheet(workbook, allItemsSheet, 'All Items');
  
  // Sheet 3: Variances Only
  const varianceItems = data.countedItems.filter(item => item.variance !== 0);
  if (varianceItems.length > 0) {
    const varianceData = varianceItems.map(item => ({
      'Item Code': item.itemCode,
      'Description': item.description,
      'Category': item.category,
      'System Qty': item.systemQty,
      'Counted Qty': item.countedQty,
      'Variance': item.variance,
      'Variance Type': item.variance > 0 ? 'Overage' : 'Shortage',
      'Counted By': item.countedBy,
    }));
    
    const varianceSheet = XLSX.utils.json_to_sheet(varianceData);
    varianceSheet['!cols'] = [
      { wch: 15 },
      { wch: 30 },
      { wch: 15 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 15 },
      { wch: 20 },
    ];
    
    XLSX.utils.book_append_sheet(workbook, varianceSheet, 'Variances');
  }
  
  // Generate filename and download
  const filename = `stock-take-${reference.replace(/\s+/g, '-').toLowerCase()}-${getCurrentDateForFilename()}.xlsx`;
  XLSX.writeFile(workbook, filename);
};

// Export stock take to CSV
export const exportStockTakeToCSV = (
  data: StockTakeExportData,
  reference: string
): void => {
  // Prepare CSV data
  const csvRows: string[] = [];
  
  // Header section
  csvRows.push('Stock Take Report');
  csvRows.push('');
  csvRows.push(`Reference,${reference}`);
  csvRows.push(`Count Date,${new Date(data.setupData.countDate).toLocaleDateString()}`);
  csvRows.push(`Warehouse,${data.setupData.warehouse || 'All Warehouses'}`);
  csvRows.push(`Assigned To,${data.setupData.assignedTo || 'N/A'}`);
  csvRows.push(`Generated,${new Date().toLocaleString()}`);
  csvRows.push('');
  
  // Statistics section
  csvRows.push('Summary Statistics');
  csvRows.push(`Total Items,${data.statistics.totalItems}`);
  csvRows.push(`Items Matching,${data.statistics.itemsMatching} (${((data.statistics.itemsMatching / data.statistics.totalItems) * 100).toFixed(1)}%)`);
  csvRows.push(`Total Shortage,${data.statistics.totalShortage} units (${data.statistics.shortageItems} items)`);
  csvRows.push(`Total Overage,${data.statistics.totalOverage} units (${data.statistics.overageItems} items)`);
  csvRows.push('');
  
  // Notes section
  if (data.notes) {
    csvRows.push('Notes');
    csvRows.push(`"${data.notes.replace(/"/g, '""')}"`);
    csvRows.push('');
  }
  
  // Items section
  csvRows.push('Item Code,Description,Category,Warehouse,Unit,System Qty,Counted Qty,Variance,Variance Status,Counted By');
  
  data.countedItems.forEach(item => {
    const varianceStatus = item.variance === 0 ? 'Match' : item.variance > 0 ? 'Overage' : 'Shortage';
    csvRows.push(
      `"${item.itemCode}","${item.description}","${item.category}","${item.warehouse}","${item.unit}",${item.systemQty},${item.countedQty},${item.variance},"${varianceStatus}","${item.countedBy}"`
    );
  });
  
  // Create CSV content
  const csvContent = csvRows.join('\n');
  
  // Create Blob and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const filename = `stock-take-${reference.replace(/\s+/g, '-').toLowerCase()}-${getCurrentDateForFilename()}.csv`;
  
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  
  // Clean up
  URL.revokeObjectURL(link.href);
};
